/**
 * EVENTSFLO-QUOTE-INVOICE-SEND-RECIPIENT-CONFIRMATION-V1
 *
 * Reusable email-recipient parser. Accepts comma-separated input in
 * any of the following formats and returns a normalised list of
 * `{ name, email, valid, raw }` records:
 *
 *   plain@example.com
 *   Name <plain@example.com>
 *   Name plain@example.com
 *   Name (plain@example.com)
 *   Multiple, comma, separated.
 *
 * Deduplicates by lowercased email. Invalid entries are returned
 * with `valid: false` so the UI can render them clearly.
 */

// Loose but practical RFC-5322-inspired regex — matches what the
// backend's `validate_explicit_recipients` accepts.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function isValidEmail(v) {
  if (!v || typeof v !== "string") return false;
  return EMAIL_RE.test(v.trim());
}

/**
 * Extract the {name, email} tuple from a single free-form entry.
 * Never throws — returns `{ valid: false }` for junk input.
 */
export function parseSingleRecipient(entry) {
  const raw = (entry || "").trim();
  if (!raw) return { name: "", email: "", valid: false, raw };

  // Angle-bracket form: Name <email@x.com>
  const angle = raw.match(/^(.*?)\s*<\s*([^<>]+?)\s*>\s*$/);
  if (angle) {
    const name = angle[1].trim().replace(/^"|"$/g, "");
    const email = angle[2].trim();
    return { name, email, valid: isValidEmail(email), raw };
  }

  // Parenthesised form: Name (email@x.com)
  const paren = raw.match(/^(.*?)\s*\(\s*([^()]+?)\s*\)\s*$/);
  if (paren) {
    const name = paren[1].trim();
    const email = paren[2].trim();
    return { name, email, valid: isValidEmail(email), raw };
  }

  // Space-separated form: Name email@x.com  (last token is the email)
  const tokens = raw.split(/\s+/);
  if (tokens.length > 1) {
    const last = tokens[tokens.length - 1];
    if (isValidEmail(last)) {
      return {
        name: tokens.slice(0, -1).join(" ").trim(),
        email: last,
        valid: true,
        raw,
      };
    }
  }

  // Bare email
  return { name: "", email: raw, valid: isValidEmail(raw), raw };
}

/**
 * Parse a comma-separated list of recipient strings.
 * Deduplicates by lowercased email — keeps the FIRST occurrence
 * (which usually carries the display name).
 */
export function parseRecipients(input) {
  if (!input) return [];
  const parts = String(input)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const rec = parseSingleRecipient(p);
    const key = (rec.email || "").toLowerCase();
    if (!key || seen.has(key)) {
      // If invalid AND empty email — surface as a distinct invalid
      // row instead of silently dropping.
      if (!rec.valid && !seen.has(rec.raw.toLowerCase())) {
        seen.add(rec.raw.toLowerCase());
        out.push(rec);
      }
      continue;
    }
    seen.add(key);
    out.push(rec);
  }
  return out;
}

/**
 * Merge a list of pre-selected contact emails + manually-typed
 * entries into a single deduplicated recipient list. Contacts win
 * on name conflict (they carry the trusted display name).
 */
export function mergeRecipients(selectedContacts, manualParsed) {
  const out = [];
  const seen = new Set();
  for (const c of selectedContacts || []) {
    const email = (c.email || "").trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      name: c.name || c.first_name || "",
      email,
      valid: isValidEmail(email),
      source: "client_contact",
    });
  }
  for (const m of manualParsed || []) {
    if (!m.email) {
      out.push({ ...m, source: "manual" });
      continue;
    }
    const key = m.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...m, source: "manual" });
  }
  return out;
}

export default parseRecipients;
